import React from 'react';
import { AuroraInput } from './AuroraInput';
import { BosLocationInput } from './BosLocationInput';
import { DesignNotesInput } from './DesignNotesInput';
import { GdriveLinkInput } from './GdriveLinkInput';
import { IbcInput } from './IbcInput';
import { IfcInput } from './IfcInput';
import { IrcInput } from './IrcInput';
import { InstallNotesInput } from './InstallNotesInput';
import { LgcyLisInput } from './LgcyLisInput';
import { MiscellaneousNotesInput } from './MiscellaneousNotesInput';
import { NecInput } from './NecInput';
import { NotionAhjInput } from './NotionAhjInput';
import { NotionCxInput } from './NotionCxInput';
import { NotionUxInput } from './NotionUxInput';
import { PoiNotesInput } from './PoiNotesInput';

interface DocumentationStepProps {
  isEnabled: boolean;
  uiState: any;
  onNecChange: (value: string) => void;
  onIbcChange: (value: string) => void;
  onIrcChange: (value: string) => void;
  onIfcChange: (value: string) => void;
  onAhjChange: (value: string) => void;
  onUmNumberChange: (value: string) => void;
  onAuroraLinkChange: (value: string) => void;
  onGdriveLinkChange: (value: string) => void;
  onInstallNotesChange: (value: string) => void;
  onMiscellaneousNotesChange: (value: string) => void;
  onNotionCxChange: (value: string) => void;
  onNotionUxChange: (value: string) => void;
  onNotionAhjChange: (value: string) => void;
  onBosLocationChange: (value: string) => void;
  onPoiNotesChange: (value: string) => void;
  onDesignNotesChange: (value: string) => void;
  onLgcyLisChange: (value: string) => void;
}

export const DocumentationStep: React.FC<DocumentationStepProps> = ({
  isEnabled,
  uiState,
  onNecChange,
  onIbcChange,
  onIrcChange,
  onIfcChange,
  onAhjChange,
  onUmNumberChange,
  onAuroraLinkChange,
  onGdriveLinkChange,
  onInstallNotesChange,
  onMiscellaneousNotesChange,
  onNotionCxChange,
  onNotionUxChange,
  onNotionAhjChange,
  onBosLocationChange,
  onPoiNotesChange,
  onDesignNotesChange,
  onLgcyLisChange
}) => {
  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Codes & Compliance */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-violet-400 h-full overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-violet-100/50 border-b border-slate-200/60">
            <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6M7 4h7l3 3v13a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" />
            </svg>
            <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Codes & Compliance</span>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              <NecInput
                onNecChange={onNecChange}
                necValue={uiState.necValue}
                isEnabled={isEnabled}
              />
              <IbcInput
                onIbcChange={onIbcChange}
                ibcValue={uiState.ibcValue}
                isEnabled={isEnabled}
              />
              <IrcInput
                onIrcChange={onIrcChange}
                ircValue={uiState.ircValue}
                isEnabled={isEnabled}
              />
              <IfcInput
                onIfcChange={onIfcChange}
                ifcValue={uiState.ifcValue}
                isEnabled={isEnabled}
              />
            </div>
          </div>
        </div>

        {/* Project Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-violet-400 h-full overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-violet-100/50 border-b border-slate-200/60">
            <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16M4 12h16M4 17h10" />
            </svg>
            <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Project Info</span>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              <BosLocationInput
                onBosLocationChange={onBosLocationChange}
                bosLocationValue={uiState.bosLocationValue}
                isEnabled={isEnabled}
              />
              <LgcyLisInput
                onLgcyLisChange={onLgcyLisChange}
                lgcyLisValue={uiState.lgcyLisValue}
                isEnabled={isEnabled}
              />
            </div>
          </div>
        </div>

        {/* External Links */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-violet-400 h-full overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-violet-100/50 border-b border-slate-200/60">
            <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.328-6.328a4 4 0 015.656 5.656l-1.5 1.5" />
            </svg>
            <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">External Links</span>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              <AuroraInput
                onAuroraChange={onAuroraLinkChange}
                auroraValue={uiState.auroraLinkValue}
                isEnabled={isEnabled}
              />
              <GdriveLinkInput
                onGdriveLinkChange={onGdriveLinkChange}
                gdriveLinkValue={uiState.gdriveLinkValue}
                isEnabled={isEnabled}
              />
              <NotionCxInput
                onNotionCxChange={onNotionCxChange}
                notionCxValue={uiState.notionCxValue}
                isEnabled={isEnabled}
              />
              <NotionUxInput
                onNotionUxChange={onNotionUxChange}
                notionUxValue={uiState.notionUxValue}
                isEnabled={isEnabled}
              />
              <NotionAhjInput
                onNotionAhjChange={onNotionAhjChange}
                notionAhjValue={uiState.notionAhjValue}
                isEnabled={isEnabled}
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 border-l-[3px] border-l-violet-400 h-full overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-violet-100/50 border-b border-slate-200/60">
            <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5h6M9 9h6M5 5h2v14H5zM17 5h2v14h-2z" />
            </svg>
            <span className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Notes</span>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              <InstallNotesInput
                onInstallNotesChange={onInstallNotesChange}
                installNotesValue={uiState.installNotesValue}
                isEnabled={isEnabled}
              />
              <PoiNotesInput
                onPoiNotesChange={onPoiNotesChange}
                poiNotesValue={uiState.poiNotesValue}
                isEnabled={isEnabled}
              />
              <DesignNotesInput
                onDesignNotesChange={onDesignNotesChange}
                designNotesValue={uiState.designNotesValue}
                isEnabled={isEnabled}
              />
              <MiscellaneousNotesInput
                onMiscellaneousNotesChange={onMiscellaneousNotesChange}
                miscellaneousNotesValue={uiState.miscellaneousNotesValue}
                isEnabled={isEnabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
